import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { LearningCharts } from "@/components/LearningCharts";
import PageIntroCard from "@/components/PageIntroCard";
import {
  Download, Target, AlertTriangle, BookOpen, Layers, CheckCircle2, XCircle,
  TrendingUp, Sparkles, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const accColor = (acc) => acc >= 80 ? "emerald" : acc >= 60 ? "amber" : "rose";

function WeakList({ title, icon: Icon, items, emptyLabel, testId }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-rose-600" />
        <h3 className="font-display font-bold text-base">{title}</h3>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400 italic">{emptyLabel}</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it, i) => {
            const c = accColor(it.accuracy);
            return (
              <li key={it.id || it.name || `row-${i}`} className="space-y-1.5" data-testid={`${testId}-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-800 truncate">{it.name}</span>
                  <span className={`text-xs font-bold text-${c}-700 shrink-0`}>{it.accuracy}%</span>
                </div>
                <Progress value={it.accuracy} className={`h-2 [&>div]:bg-${c}-500`} />
                <div className="text-[11px] text-slate-500">{it.attempts ?? it.total} attempts</div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default function StudentAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/student/analytics")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  }, []);

  const exportReport = async () => {
    try {
      const token = localStorage.getItem("pm_token");
      const resp = await fetch(`${API}/student/learning-report/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `learning_report_${(user.full_name || "student").replace(/\s+/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not download report");
    }
  };

  if (loading) return <div className="text-slate-500" data-testid="analytics-loading">Loading analytics…</div>;
  if (!data) return null;

  const s = data.summary || {};
  const coverage = s.course_questions_total
    ? Math.round((s.unique_questions / s.course_questions_total) * 100)
    : 0;

  return (
    <div className="space-y-8" data-testid="student-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" /> Your Analytics
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Performance Dashboard
          </h1>
          <p className="text-slate-600 mt-1">
            {data.course ? <>Tracking your progress in <span className="font-semibold">{data.course.name}</span></> : "Your personal learning insights"}
          </p>
        </div>
        <Button variant="outline" onClick={exportReport} data-testid="analytics-export-btn">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <PageIntroCard
        pageKey="analytics"
        icon={BarChart3}
        tone="emerald"
        title="Read your progress at a glance"
        body="Coverage shows how much of your course you've touched. Accuracy is your correct-answer rate. The Weak Modules and Weak Questions lists pinpoint exactly what to review next."
      />

      {!data.has_data ? (
        <Card className="p-10 border-dashed border-slate-300 text-center" data-testid="analytics-empty">
          <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h3 className="font-display font-bold text-lg">No data yet</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Answer a few questions in Review Mode or Smart Review and your performance breakdown will appear here.
          </p>
          <Link to="/student/courses" className="inline-block mt-4">
            <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Browse Courses</Button>
          </Link>
        </Card>
      ) : (
        <>
          {/* Summary Tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5 border-slate-200" data-testid="analytics-tile-accuracy">
              <div className="h-10 w-10 rounded-md grid place-items-center bg-blue-50 text-blue-700">
                <Target className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="mt-4 text-3xl font-display font-bold">{s.overall_accuracy}%</div>
              <div className="text-sm text-slate-500 mt-1">Overall accuracy</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{s.total_correct} of {s.total_attempts} attempts</div>
            </Card>
            <Card className="p-5 border-slate-200" data-testid="analytics-tile-mastered">
              <div className="h-10 w-10 rounded-md grid place-items-center bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="mt-4 text-3xl font-display font-bold">{s.mastered}</div>
              <div className="text-sm text-slate-500 mt-1">Questions mastered (L5)</div>
            </Card>
            <Card className="p-5 border-slate-200" data-testid="analytics-tile-repeated">
              <div className="h-10 w-10 rounded-md grid place-items-center bg-rose-50 text-rose-700">
                <XCircle className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="mt-4 text-3xl font-display font-bold">{s.repeatedly_missed}</div>
              <div className="text-sm text-slate-500 mt-1">Repeatedly missed</div>
              {s.chronic_miss > 0 && (
                <Badge className="mt-2 bg-rose-100 text-rose-800 border-rose-200">{s.chronic_miss} chronic</Badge>
              )}
            </Card>
            <Card className="p-5 border-slate-200" data-testid="analytics-tile-coverage">
              <div className="h-10 w-10 rounded-md grid place-items-center bg-indigo-50 text-indigo-700">
                <Layers className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="mt-4 text-3xl font-display font-bold">{s.unique_questions}</div>
              <div className="text-sm text-slate-500 mt-1">Unique questions touched</div>
              {s.course_questions_total > 0 && (
                <div className="mt-2">
                  <Progress value={coverage} className="h-1.5" />
                  <div className="text-[11px] text-slate-400 mt-1">{coverage}% of {s.course_questions_total} in course</div>
                </div>
              )}
            </Card>
          </div>

          {/* Weak Areas */}
          <div>
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Where to focus
            </h2>
            <div className="grid lg:grid-cols-2 gap-4">
              <WeakList
                title="Weakest Topics"
                icon={AlertTriangle}
                items={data.weakest_topics || []}
                emptyLabel="Tag your questions with topics to surface weak areas."
                testId="weakest-topics"
              />
              <WeakList
                title="Weakest Modules"
                icon={BookOpen}
                items={data.weakest_modules || []}
                emptyLabel="Answer at least 3 questions per module."
                testId="weakest-modules"
              />
              <WeakList
                title="Weakest Subjects"
                icon={Layers}
                items={data.weakest_subjects || []}
                emptyLabel="Answer at least 5 questions per subject."
                testId="weakest-subjects"
              />
              <WeakList
                title="Weakest Competencies"
                icon={Target}
                items={data.weakest_competencies || []}
                emptyLabel="Tag your questions with competencies to track them."
                testId="weakest-competencies"
              />
            </div>
          </div>

          {/* Charts */}
          <div data-testid="analytics-charts-section">
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" /> Visual breakdown
            </h2>
            <LearningCharts data={data} testIdPrefix="analytics-" />
          </div>

          <Card className="p-6 border-slate-200 bg-gradient-to-br from-indigo-50 to-blue-50">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" /> Turn insights into action
                </h3>
                <p className="text-sm text-slate-600 mt-1">Let Smart Review prioritize your weak areas in your next session.</p>
              </div>
              <Link to="/student/smart-review">
                <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="analytics-start-smart-review-btn">
                  Start Smart Review
                </Button>
              </Link>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
